import React, { useState, useRef, useEffect } from 'react';
import { 
  TextField, 
  IconButton, 
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Html5QrcodeScanner } from 'html5-qrcode';

/**
 * A text field with integrated barcode scanning functionality
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the text field
 * @param {string} props.name - Name for the form field
 * @param {string} props.value - Current value
 * @param {function} props.onChange - Change handler
 * @param {function} props.onScan - Function to call when scan is complete
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.fullWidth - Whether the field should take full width
 * @returns {JSX.Element} - A TextField with barcode scanning capability
 */
const BarcodeScanner = ({ 
  label, 
  name, 
  value, 
  onChange, 
  onScan,
  required = false, 
  disabled = false, 
  placeholder = '', 
  fullWidth = true,
  ...rest
}) => {
  const [scanning, setScanning] = useState(false);
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [scannerInitialized, setScannerInitialized] = useState(false);
  const inputRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const html5QrcodeScanner = useRef(null);
  const qrCodeRegionId = useRef(`qr-reader-${Math.random().toString(36).substr(2, 9)}`);

  // Reset scanning mode when unmounted
  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      if (html5QrcodeScanner.current) {
        html5QrcodeScanner.current.clear().catch(console.error);
      }
    };
  }, []);

  const initializeScanner = () => {
    if (html5QrcodeScanner.current || scannerInitialized) {
      return;
    }

    try {
      html5QrcodeScanner.current = new Html5QrcodeScanner(
        qrCodeRegionId.current,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true,
          defaultZoomValueIfSupported: 1,
          disableFlip: false,
        },
        /* verbose= */ false
      );

      html5QrcodeScanner.current.render(
        (decodedText, decodedResult) => {
          // Handle successful scan
          console.log(`Code matched = ${decodedText}`, decodedResult);
          
          // Update the value through onChange
          if (onChange) {
            const event = {
              target: {
                name: name,
                value: decodedText
              }
            };
            onChange(event);
          }
          
          // Call onScan callback if provided
          if (onScan) {
            onScan(decodedText);
          }
          
          // Close scanner after successful scan
          handleCloseDialog();
        },
        (error) => {
          // Handle scan errors (these are usually just "no QR code found" messages)
          // console.warn(`Code scan error = ${error}`);
        }
      );
      
      setScannerInitialized(true);
    } catch (error) {
      console.error('Error initializing QR code scanner:', error);
    }
  };

  const handleStartScan = () => {
    if (disabled) return;
    
    setScannerDialogOpen(true);
    setScanning(true);
    
    // Initialize scanner after dialog opens
    setTimeout(() => {
      initializeScanner();
    }, 300);
  };

  const handleCloseDialog = () => {
    setScannerDialogOpen(false);
    setScanning(false);
    setScannerInitialized(false);
    
    // Clean up scanner
    if (html5QrcodeScanner.current) {
      html5QrcodeScanner.current.clear().catch(console.error);
      html5QrcodeScanner.current = null;
    }
    
    // Ensure we refocus on the main input after closing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleKeyPress = (e) => {
    // If Enter key is pressed and we're scanning
    if (e.key === 'Enter' && scanning) {
      e.preventDefault();
      
      // If a scan handler was provided, call it
      if (onScan && value) {
        onScan(value);
      }
      
      // Close dialog after scan is completed
      handleCloseDialog();
    }
  };

  const handleManualInput = () => {
    if (onScan && value) {
      onScan(value);
    }
    handleCloseDialog();
  };

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        required={required}
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        inputRef={inputRef}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton 
                onClick={handleStartScan}
                disabled={disabled}
                edge="end"
                color="primary"
                sx={{ 
                  '&:hover': { 
                    bgcolor: 'rgba(25, 118, 210, 0.04)' 
                  },
                  p: 1
                }}
              >
                <Tooltip title="Scan barcode">
                  <QrCodeScannerIcon color={scanning ? "primary" : "primary"} />
                </Tooltip>
              </IconButton>
            </InputAdornment>
          ),
        }}
        {...rest}
      />

      {/* Scanner Dialog */}
      <Dialog
        open={scannerDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="scanner-dialog-title"
        maxWidth="md"
        fullWidth
        keepMounted={false}
      >
        <DialogTitle id="scanner-dialog-title">
          Scan Barcode or QR Code
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Typography variant="body1" gutterBottom>
              Position the barcode or QR code within the camera view:
            </Typography>
            
            {/* QR Code Scanner Container */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                mt: 2, 
                mb: 2, 
                backgroundColor: '#f8f9fa',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div 
                id={qrCodeRegionId.current} 
                style={{ 
                  width: '100%',
                  maxWidth: '500px'
                }}
              />
              
              {!scannerInitialized && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  <CircularProgress size={30} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Initializing camera...
                  </Typography>
                </Box>
              )}
            </Paper>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Or enter the code manually:
            </Typography>
            
            <TextField
              fullWidth
              label={label || "Barcode"}
              name={name}
              value={value}
              onChange={onChange}
              onKeyPress={handleKeyPress}
              sx={{ mt: 1 }}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleManualInput} variant="contained" color="primary" disabled={!value}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BarcodeScanner;